import { createAnimation } from '@ionic/vue';

import _ from 'lodash';

const POPOVER_IOS_BODY_PADDING = 5;
const MINIMUM_SPACE_LIMIT = 400; //Any space with less height than this will get treated differently (popover fills whole screen)

export const adjustPopoverPosition = (baseEl, ev, overrideContentDimensions = undefined) => {
  let originY = 'top';
  let originX = 'left';
  const contentEl = baseEl.querySelector('.popover-content');
  const viewportEl = baseEl.querySelector('.popover-viewport');
  const contentDimentions = overrideContentDimensions || viewportEl.getBoundingClientRect(); //Take viewport size instead of content size for out of bounds calculation. Viewport depends on inner content if not set to 100% height. Outer content element is resized to fit the screen.
  const contentWidth = contentDimentions.width;
  const contentHeight = contentDimentions.height;
  const bodyWidth = baseEl.ownerDocument.defaultView.innerWidth;
  const bodyHeight = baseEl.ownerDocument.defaultView.innerHeight;
  // If ev was passed, use that for target element
  const targetDim = ev && ev.target && ev.target.getBoundingClientRect();
  const targetTop = targetDim != null && 'top' in targetDim ? targetDim.top : bodyHeight / 2 - contentHeight / 2;
  const targetLeft = targetDim != null && 'left' in targetDim ? targetDim.left : bodyWidth / 2;
  const targetWidth = (targetDim && targetDim.width) || 0;
  const targetHeight = (targetDim && targetDim.height) || 0;
  const arrowEl = baseEl.querySelector('.popover-arrow');
  const arrowDim = arrowEl.getBoundingClientRect();
  const arrowWidth = arrowDim.width;
  const arrowHeight = arrowDim.height;
  const edgeSnapHysteresis = 10; //Offset to make sure it is inside the safe area and does not flicker back and forth
  if (targetDim == null) {
    arrowEl.style.display = 'none';
  } else {
    arrowEl.style.display = '';
  }
  const arrowCSS = {
    top: targetTop + targetHeight,
    topUnit: 'px',
    left: targetLeft + targetWidth / 2 - arrowWidth / 2,
    leftUnit: 'px',
    bottomUnit: 'px',
    rightUnit: 'px'
  };
  const popoverCSS = {
    top: targetTop + targetHeight + (arrowHeight - 1),
    topUnit: 'px',
    left: targetLeft + targetWidth / 2 - contentWidth / 2,
    leftUnit: 'px',
    bottomUnit: 'px',
    rightUnit: 'px'
  };
  //Get and parse safe area that should be in px
  const computedStyles = getComputedStyle(contentEl);
  let safeAreaTop = computedStyles.getPropertyValue('--ion-safe-area-top');
  if (safeAreaTop != null && _.endsWith(safeAreaTop, 'px')) {
    safeAreaTop = parseFloat(safeAreaTop.replaceAll(/[^0-9.]/g, '')); //Remove all non-digit characters and parse
  } else {
    safeAreaTop = 0;
  }
  safeAreaTop += ((POPOVER_IOS_BODY_PADDING / 100) * bodyHeight); //Include padding like when snapping to the edge below for calulcation
  let safeAreaBottom = computedStyles.getPropertyValue('--ion-safe-area-bottom');
  if (safeAreaBottom != null && _.endsWith(safeAreaBottom, 'px')) {
    safeAreaBottom = parseFloat(safeAreaBottom.replaceAll(/[^0-9.]/g, '')); //Remove all non-digit characters and parse
  } else {
    safeAreaBottom = 0;
  }
  safeAreaBottom += ((POPOVER_IOS_BODY_PADDING / 100) * bodyHeight); //Include padding like when snapping to the edge below for calulcation
  // If the popover left is less than the padding it is off screen
  // to the left so adjust it, else if the width of the popover
  // exceeds the body width it is off screen to the right so adjust
  //
  let checkSafeAreaLeft = false;
  let checkSafeAreaRight = false;
  // If the popover left is less than the padding it is off screen
  // to the left so adjust it, else if the width of the popover
  // exceeds the body width it is off screen to the right so adjust
  // 25 is a random/arbitrary number. It seems to work fine for ios11
  // and iPhoneX. Is it perfect? No. Does it work? Yes.
  if (popoverCSS.left < POPOVER_IOS_BODY_PADDING + 25) {
    checkSafeAreaLeft = true;
    popoverCSS.left = POPOVER_IOS_BODY_PADDING;
  }
  else if (contentWidth + POPOVER_IOS_BODY_PADDING + popoverCSS.left + 25 > bodyWidth) {
    // Ok, so we're on the right side of the screen,
    // but now we need to make sure we're still a bit further right
    // cus....notchurally... Again, 25 is random. It works tho
    checkSafeAreaRight = true;
    popoverCSS.left = bodyWidth - contentWidth - POPOVER_IOS_BODY_PADDING;
    originX = 'right';
  }

  const bottomSpace = (bodyHeight - safeAreaBottom) - (targetTop + targetHeight); /* Available space minus the space until the bottom edge of the target element starts */
  const topSpace = (targetTop - safeAreaTop); /* The space from the top edge of the screen to the top edge of the target element minus the safe area */
  const minimumContentSize = Math.min(MINIMUM_SPACE_LIMIT, contentHeight); //If the original content size is already smaller, use this as the limit. Remove contentHeight here again, if resizing creates issues in the future.

  //If screen space is too small (e.g. landscape) based on available spaces, use the whole available space of the screen by sticking to the edges (minus the safe areas) and hide the arrow
  if ((bottomSpace < minimumContentSize && topSpace < minimumContentSize) || (targetTop > (bodyHeight - edgeSnapHysteresis))) { //No space makes it fit or the target got moved outside of the screen by the resize, use whole screen
    popoverCSS.topUnit = undefined; //Set directly not in px
    popoverCSS.top = undefined; //By default just on the bottom and as much space as it takes
    //If it is bigger than the available space, limit on the top too!
    if (contentHeight > (bodyHeight - safeAreaTop - safeAreaBottom - edgeSnapHysteresis)) {
      popoverCSS.top = `calc(${POPOVER_IOS_BODY_PADDING}% + var(--ion-safe-area-top, 0px))`; //Makes the top stick to the upper edge of the screen
    }
    popoverCSS.bottom = `calc(${POPOVER_IOS_BODY_PADDING}% + var(--ion-safe-area-bottom, 0px))`; //Makes the bottom stick to the lower edge of the screen
    popoverCSS.bottomUnit = undefined; //Set directly not in px
    arrowEl.style.display = 'none'; //Hide arrow when whole screen is used
  } else {
    // make it pop up if the space above got bigger than below (more space is always better - prevents keyboard popup squeezing it out of the way)
    if (topSpace > bottomSpace) {
      arrowCSS.top = undefined;
      popoverCSS.top = undefined;
      arrowCSS.bottom = bodyHeight - targetTop;
      popoverCSS.bottom = bodyHeight - targetTop + (arrowHeight - 1)

      baseEl.classList.add('popover-bottom'); //Move the arrow to the bottom, when opened up
      originY = 'bottom';

      // If there is not enough room above, snap to the top edge with padding
      if (targetTop - arrowHeight - contentHeight < safeAreaTop + edgeSnapHysteresis) {
        popoverCSS.top = `calc(${POPOVER_IOS_BODY_PADDING}% + var(--ion-safe-area-top, 0px))`; //Makes the top stick to the upper edge of the screen
        popoverCSS.topUnit = undefined; //Set directly not in px
      }
    } else {
      //Remove the bottom class in this case - this indicates where the arrow is (here on the top)
      baseEl.classList.remove('popover-bottom');

      // If there is not enough room below, snap to the bottom edge with padding
      if (targetTop + targetHeight + arrowHeight + contentHeight > (bodyHeight - (safeAreaBottom + edgeSnapHysteresis))) { 
        popoverCSS.bottom = `calc(${POPOVER_IOS_BODY_PADDING}% + var(--ion-safe-area-bottom, 0px))`; //Makes the bottom stick to the lower edge of the screen
        popoverCSS.bottomUnit = undefined; //Set directly not in px
      }
    }
  }

  arrowEl.style.top = (arrowCSS.top != null) ? (arrowCSS.top + (arrowCSS.topUnit || '')) : '';
  arrowEl.style.left = (arrowCSS.left != null) ? (arrowCSS.left + (arrowCSS.leftUnit || '')) : '';
  arrowEl.style.bottom = (arrowCSS.bottom != null) ? (arrowCSS.bottom + (arrowCSS.bottomUnit || '')) : '';
  contentEl.style.top = (popoverCSS.top != null) ? (popoverCSS.top + (popoverCSS.topUnit || '')) : '';
  contentEl.style.left = (popoverCSS.left != null) ? (popoverCSS.left + (popoverCSS.leftUnit || '')) : '';
  contentEl.style.bottom = (popoverCSS.bottom != null) ? (popoverCSS.bottom + (popoverCSS.bottomUnit || '')) : '';
  if (checkSafeAreaLeft) {
    contentEl.style.left = `calc(${popoverCSS.left}px + var(--ion-safe-area-left, 0px))`;
  }
  if (checkSafeAreaRight) {
    contentEl.style.left = `calc(${popoverCSS.left}px - var(--ion-safe-area-right, 0px))`;
  }
  contentEl.style.transformOrigin = originY + ' ' + originX;
}

export const iosEnterAnimation = (baseEl, ev) => {
  adjustPopoverPosition(baseEl, ev);
  const baseAnimation = createAnimation();
  const backdropAnimation = createAnimation();
  const wrapperAnimation = createAnimation();
  backdropAnimation
    .addElement(baseEl.querySelector('ion-backdrop'))
    .fromTo('opacity', 0.01, 'var(--backdrop-opacity)')
    .beforeStyles({
    'pointer-events': 'none'
  })
    .afterClearStyles(['pointer-events']);
  wrapperAnimation
    .addElement(baseEl.querySelector('.popover-wrapper'))
    .fromTo('opacity', 0.01, 1);
  return baseAnimation
    .addElement(baseEl)
    .easing('ease')
    .duration(100)
    .addAnimation([backdropAnimation, wrapperAnimation]);
};

/**
 * iOS Popover Leave Animation
 */
export const iosLeaveAnimation = (baseEl) => {
  const baseAnimation = createAnimation();
  const backdropAnimation = createAnimation();
  const wrapperAnimation = createAnimation();
  backdropAnimation
    .addElement(baseEl.querySelector('ion-backdrop'))
    .fromTo('opacity', 'var(--backdrop-opacity)', 0);
  wrapperAnimation
    .addElement(baseEl.querySelector('.popover-wrapper'))
    .fromTo('opacity', 0.99, 0);
  return baseAnimation
    .addElement(baseEl)
    .easing('ease')
    .duration(500)
    .addAnimation([backdropAnimation, wrapperAnimation]);
};